import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { 
  Cog6ToothIcon,
  PlayIcon,
  DocumentIcon,
  CheckCircleIcon,
  ClockIcon,
  ServerIcon
} from '@heroicons/react/24/outline';

import PipelineRunSelectionModal from './PipelineRunSelectionModal';
import type { 
  ReleaseNodePipelineRunDto 
} from '../../types/release';

interface ReleaseCustomNodeProps {
  id: string;
  data: {
    label: string;
    nodeType: string;
    description?: string;
    isActive?: boolean;
    backgroundColor?: string;
    borderColor?: string;
    width?: number;
    height?: number;
    pipelineAssociations?: Array<{
      id: string;
      pipelineId: string;
      pipelineDefinitionId: number;
      pipelineName: string;
    }>;
    releaseId?: string;
    nodeId?: string;
    releaseNodePipelineRuns?: ReleaseNodePipelineRunDto[];
    readOnly?: boolean;
  };
  selected?: boolean;
}

const getNodeIcon = (nodeType: string) => {
  switch (nodeType) {
    case 'pipeline':
      return <PlayIcon className="w-4 h-4" />;
    case 'approval':
      return <CheckCircleIcon className="w-4 h-4" />;
    case 'manual':
      return <DocumentIcon className="w-4 h-4" />;
    case 'wait':
      return <ClockIcon className="w-4 h-4" />;
    case 'service':
      return <ServerIcon className="w-4 h-4" />;
    default:
      return <Cog6ToothIcon className="w-4 h-4" />;
  }
};

const getNodeColors = (
  nodeType: string,
  customBackgroundColor?: string,
  customBorderColor?: string
) => {
  if (customBackgroundColor && customBorderColor) {
    return { backgroundColor: customBackgroundColor, borderColor: customBorderColor };
  }

  switch (nodeType) {
    case 'pipeline':
      return { backgroundColor: '#dcfce7', borderColor: '#16a34a' };
    case 'approval':
      return { backgroundColor: '#fef3c7', borderColor: '#d97706' };
    case 'manual':
      return { backgroundColor: '#dbeafe', borderColor: '#2563eb' };
    case 'wait':
      return { backgroundColor: '#fce7f3', borderColor: '#ec4899' };
    case 'service':
      return { backgroundColor: '#f3e8ff', borderColor: '#a855f7' };
    default:
      return { backgroundColor: '#f3f4f6', borderColor: '#6b7280' };
  }
};

export const ReleaseCustomNode: React.FC<ReleaseCustomNodeProps> = ({ 
  id, 
  data, 
  selected 
}) => {
  const [showPipelineRunModal, setPipelineRunModal] = useState(false);
  
  const { backgroundColor, borderColor } = getNodeColors(
    data.nodeType,
    data.backgroundColor,
    data.borderColor
  );

  const pipelineAssociations = data.pipelineAssociations || [];
  const hasPipeline = pipelineAssociations.length > 0;
  const readOnly = data.readOnly || false;

  // Get the primary pipeline association for display
  const primaryPipeline = pipelineAssociations.length > 0 ? pipelineAssociations[0] : null;
  
  // Check if this node has pipeline runs selected
  const nodeId = data.nodeId || id;
  const nodePipelineRuns = data.releaseNodePipelineRuns?.filter(
    run => run.releaseFlowNodeId === nodeId && run.isSelected
  ) || [];
  const hasSelectedPipelineRun = nodePipelineRuns.length > 0;
  const selectedRun = nodePipelineRuns[0]; // Get the first selected run

  // Determine display name
  const displayName = data.nodeType === 'pipeline' && primaryPipeline 
    ? primaryPipeline.pipelineName 
    : data.label;

  // For pipeline nodes in release view: show pipeline run info if available
  let displayText = displayName;
  let subText = '';
  
  if (data.nodeType === 'pipeline') {
    if (!hasPipeline) {
      displayText = 'No Pipeline';
      subText = 'Pipeline not configured';
    } else if (hasSelectedPipelineRun && selectedRun) {
      displayText = primaryPipeline?.pipelineName || data.label;
      // Extract build number from pipeline run name or use ID
      const buildMatch = selectedRun.buildName?.match(/#(\d+)/) || selectedRun.buildName || selectedRun.id.substring(0, 8);
      subText = `Run: ${Array.isArray(buildMatch) ? `#${buildMatch[1]}` : `#${buildMatch}`}`;
    } else {
      displayText = primaryPipeline?.pipelineName || data.label;
      subText = 'No run selected';
    }
  }

  const handleNodeClick = (e: React.MouseEvent) => {
    // Only allow pipeline run selection for pipeline nodes with pipelines in release context
    if (data.nodeType === 'pipeline' && hasPipeline && !readOnly && data.releaseId) {
      e.stopPropagation();
      setPipelineRunModal(true);
    }
  };

  const handleModalClose = () => {
    setPipelineRunModal(false);
  };

  // Determine node styling based on state
  const needsPipelineRun = data.nodeType === 'pipeline' && hasPipeline && !hasSelectedPipelineRun;
  const nodeBackgroundColor = needsPipelineRun ? '#fef2f2' : backgroundColor; // Light red if needs pipeline run
  const nodeBorderColor = needsPipelineRun ? '#f87171' : borderColor; // Red border if needs pipeline run

  // Determine cursor style
  const cursorStyle = data.nodeType === 'pipeline' && hasPipeline && !readOnly && data.releaseId ? 'cursor-pointer' : 'cursor-default';

  return (
    <>
      <div
        className={`px-4 py-2 shadow-md rounded-md border-2 min-w-0 ${
          selected ? 'ring-2 ring-blue-500' : ''
        } ${cursorStyle}`}
        style={{
          backgroundColor: nodeBackgroundColor,
          borderColor: nodeBorderColor,
          width: data.width || 180,
          height: data.height || 60,
        }}
        onClick={handleNodeClick}
      >
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 !bg-gray-400 !border-gray-600"
        />

        <div className="flex items-center h-full">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="flex-shrink-0">
              {getNodeIcon(data.nodeType)}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold truncate ${
                needsPipelineRun ? 'text-red-700' : 'text-gray-900'
              }`}>
                {displayText}
              </div>
              
              {subText && (
                <div className={`text-xs truncate mt-0.5 ${
                  needsPipelineRun ? 'text-red-600' : 
                  hasSelectedPipelineRun ? 'text-green-600' :
                  'text-gray-500'
                }`}>
                  {subText}
                </div>
              )}
              
              {data.description && !subText && (
                <div className="text-xs text-gray-500 truncate mt-1">
                  {data.description}
                </div>
              )}
            </div>
          </div>
        </div>

        <Handle
          type="source"
          position={Position.Bottom}
          className="w-3 h-3 !bg-gray-400 !border-gray-600"
        />
      </div>

      {/* Pipeline Run Selection Modal */}
      {data.releaseId && primaryPipeline && (
        <PipelineRunSelectionModal
          isOpen={showPipelineRunModal}
          onClose={handleModalClose}
          releaseId={data.releaseId}
          nodeId={nodeId}
          nodeName={data.label}
          pipelineId={primaryPipeline.pipelineId}
          pipelineDefinitionId={primaryPipeline.pipelineDefinitionId}
          pipelineName={primaryPipeline.pipelineName}
          existingSelection={selectedRun}
          onSelectionChange={() => {
            // TODO: Refresh the release data to show updated pipeline run selection
            console.log('Pipeline run selection changed');
          }}
        />
      )}
    </>
  );
};

export default ReleaseCustomNode;